import { useState, useEffect } from 'react'
import { useFileIO } from '@papan/files/client/file-io'
import { useRealtime } from '@papan/realtime/client'
interface VideoViewerProps {
  fileId: string
  fileName?: string
  versionId?: string
}

export function VideoViewer({ fileId, fileName, versionId }: VideoViewerProps) {
  // Direct streaming: hand the <video> element a URL pointing at the
  // streaming /content endpoint. The browser issues Range requests against
  // the server natively (initial probe, seek-driven ranges, incremental
  // forward fetches), and HTTP-level backpressure flows through.
  //
  // The PREVIOUS implementation did `filesApi.getFileContent(fileId)` →
  // `URL.createObjectURL(blob)` → `<video src={blobUrl}>`. That fetched the
  // entire video into a JS blob before any frame could play — fine for a
  // 50 MB clip, broken for a 2 GB movie (multi-second load delay, holds the
  // whole file in browser memory, no seek-while-loading).
  //
  // URL composition routes through FileIO.getStreamingUrl: Drive returns
  // /api/files/<id>/content with tenant/org-email query params (cookies
  // travel with <video src> automatically; custom headers don't), and
  // share view returns the share-authorized /s/<sid>/<filename> URL.
  const [refreshCounter, setRefreshCounter] = useState(0)
  const { onFileEvent } = useRealtime()
  const fileIO = useFileIO()

  useEffect(() => {
    if (versionId) return
    return onFileEvent((event) => {
      if (event.fileId === fileId && (event.type === 'file:updated' || event.type === 'file:restored')) {
        setRefreshCounter(c => c + 1)
      }
    })
  }, [fileId, versionId, onFileEvent])

  let url = fileIO.getStreamingUrl(fileId, { versionId, fileName })
  // Cache-bust on realtime update so the <video> re-opens with the new
  // server content (otherwise the browser would serve from its HTTP cache
  // for the unchanged URL).
  if (refreshCounter > 0) {
    url += `${url.includes('?') ? '&' : '?'}_r=${refreshCounter}`
  }

  return (
    <div className="flex-1 flex items-center justify-center overflow-auto p-4 themed-bg-primary">
      <video
        key={refreshCounter}
        // Before `src`: the browser starts the load the moment src lands, so
        // the attribute has to already be there to pick the request mode.
        // Without it the load is no-cors and CORP kills it cross-origin (#410).
        crossOrigin={fileIO.streamingCrossOrigin()}
        src={url}
        controls
        // iOS Safari plays inline (not fullscreen-takeover) only with this;
        // `metadata` avoids fetching the whole file just to show the poster.
        playsInline
        preload="metadata"
        className="max-w-full max-h-full"
      >
        Your browser does not support the video element.
      </video>
    </div>
  )
}
